import { ApolloError } from '@apollo/client';
import { filterSidePanelEvent, useDebouncedCalendarRefreshEvent, UUID } from '@cmg/common';
import { Divider, MenuList } from '@cmg/design-system';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import React from 'react';

import { SNotFound } from '../../../../common/components/errorRenderer/SNotFound/SNotFound';
import ServerErrorsBanner from '../../../../common/components/indicators/server-error/ServerErrorsBanner';
import { useDocumentTitle } from '../../../../common/hooks/useDocumentTitle/useDocumentTitle';
import routeFactory from '../../../../common/util/routeFactory';
import { OfferingType } from '../../../../graphql';
import { parsePartialErrors } from '../../../../graphql/parsePartialErrors';
import { useSidePanelBuySideNavItems } from '../../hooks/useSidePanelBuySideNavItems';
import { SideNavbarItem } from './components/design-system/SideNavbarItem';
import { SideNavbarSubNavigationItem } from './components/design-system/SideNavbarSubNavigationItem';
import { Layout } from './components/design-system/SidePanelNavbar.styles';
import ConvertibleOfferingStatusIndicator from './components/offering-status-indicator/ConvertibleOfferingStatusIndicator';
import OfferingStatusIndicator from './components/offering-status-indicator/OfferingStatusIndicator';
import { useSideBar_OfferingDetails_Offering_BuySideQuery } from './graphql';

const EMPTY_ARRAY = [];
export type Props = {
  offeringId: UUID;
  baseSidePanelUrl: string;
};

const SidePanelBuySideNavbar: React.FC<Props> = ({ offeringId, baseSidePanelUrl }) => {
  const { data, error, loading, refetch } = useSideBar_OfferingDetails_Offering_BuySideQuery({
    variables: { offeringId },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });
  const [publishedOffering] = data?.publishedOfferings?.items ?? EMPTY_ARRAY;

  useDocumentTitle(
    routeFactory.offeringSidePanel.getDocumentTitle({
      ticker: publishedOffering?.pricingInstrumentStockSymbol,
    })
  );

  const filterFn = React.useMemo(() => {
    return filterSidePanelEvent(offeringId);
  }, [offeringId]);

  useDebouncedCalendarRefreshEvent({
    callback: React.useCallback(() => {
      refetch();
    }, [refetch]),
    filterFn,
  });

  const navItems = useSidePanelBuySideNavItems({
    baseSidePanelUrl,
    isExecutingOnPlatform: publishedOffering?.isExecutingOnPlatform,
    isLinkedOffering: !!publishedOffering?.dataLabFull?.linkedOfferingId,
  });

  if (isNil(publishedOffering) && !loading && !error) {
    return <SNotFound />;
  }

  const classifiedErrors = parsePartialErrors(error);

  if (!isEmpty(classifiedErrors.errors)) {
    return (
      <ServerErrorsBanner error={new ApolloError({ graphQLErrors: classifiedErrors.errors })} />
    );
  }

  return (
    <Layout error={false} data-testid="side-panel-buy-side-navbar">
      {loading && <div>Loading...</div>}
      {publishedOffering && (
        <React.Fragment>
          {publishedOffering.type === OfferingType.Convertible ? (
            <ConvertibleOfferingStatusIndicator publishedOffering={publishedOffering} />
          ) : (
            <OfferingStatusIndicator publishedOffering={publishedOffering} />
          )}
          <Divider />
          <MenuList disablePadding={false} disableListWrap={false} component="nav">
            {navItems.map(navItem => {
              if ('items' in navItem) {
                return <SideNavbarSubNavigationItem key={navItem.id} navItemGroup={navItem} />;
              }
              return (
                <SideNavbarItem
                  key={navItem.id}
                  icon={navItem.icon}
                  href={navItem.href}
                  selected={navItem.selected}
                  data-testid={navItem.testId}
                >
                  {navItem.label}
                </SideNavbarItem>
              );
            })}
          </MenuList>
        </React.Fragment>
      )}
    </Layout>
  );
};

export default SidePanelBuySideNavbar;
